import { FC, useState } from 'react';

import { ActEditingField } from 'utils/constants/admin';

import Button, { ButtonKind, ButtonScale } from 'bloko/blocks/button';
import DropDownClick, { DownPlacement } from 'bloko/blocks/drop/Down/Click';
import HSpacing from 'bloko/blocks/hSpacing';
import Link from 'bloko/blocks/link';
import VSpacing from 'bloko/blocks/vSpacing';

import useOnOffState from 'hooks/common/useOnOffState';
import useTrls from 'hooks/translation/useTrls';

// @ts-expect-error TS7016
import AccountLink from 'components/Admin/Invoices/InvoicesTable/Invoice/Account/AccountLink';
// @ts-expect-error TS7016
import EmployerLink from 'components/Admin/Invoices/InvoicesTable/Invoice/Account/EmployerLink';
// @ts-expect-error TS7016
import ActError from 'components/Admin/Reports/ActsReport/ActsTable/Act/ActError';
import Amount from 'components/Admin/Reports/ActsReport/ActsTable/Act/Amount';
// @ts-expect-error TS7016
import Invoices from 'components/Admin/Reports/ActsReport/ActsTable/Act/Invoices';
import ActSplitConfirmation from 'components/Admin/Reports/ActsReport/EditingActsTable/ActSplitConfirmation/ActSplitConfirmation';
import duplicateAct from 'components/Admin/Reports/ActsReport/EditingActsTable/EditingAct/duplicateAct';
import { editAct, EditActData } from 'components/Admin/Reports/ActsReport/EditingActsTable/EditingAct/editAct';
import EditingField from 'components/Admin/Reports/ActsReport/EditingActsTable/EditingAct/EditingField';
import Period from 'components/Admin/Reports/ActsReport/EditingActsTable/EditingAct/Period/Period';
import SendStatus from 'components/Admin/Reports/ActsReport/EditingActsTable/EditingAct/SendStatus/SendStatus';
import Status from 'components/Admin/Reports/ActsReport/EditingActsTable/EditingAct/Status/Status';
import { fetchActHistory } from 'components/Admin/Reports/ActsReport/EditingActsTable/fetchActHistory';
import { ActAdvertisingServiceType, ActProcessingType, ActType } from 'components/Admin/Reports/ActsReport/Labels';
import TableCell from 'components/Table/TableCell';
import TableRow from 'components/Table/TableRow';

import {
    Act,
    ActAdvertisingServiceType as ActAdvertisingServiceTypeEnum,
    ActProcessingType as ActProcessingTypeEnum,
    ActSendStatus as ActSendStatusEnum,
} from 'store/models/acts';
import { useDispatch } from 'store/utils';

import styles from 'components/Admin/Reports/ActsReport/ActsTable/Act/Act.module.less';

export type EditingData = Partial<Record<ActEditingField, string | undefined>>;

interface EditingActProps {
    act: Act;
}

const EditingAct: FC<EditingActProps> = ({ act }) => {
    const {
        actId,
        actName,
        amount,
        correctedAmount,
        accountId,
        type,
        employerId,
        invoices,
        processingType,
        advertisingServiceType,
        documentNumber,
        actSendStatus,
        error,
    } = act;

    const isAdvertisingType = advertisingServiceType === ActAdvertisingServiceTypeEnum.Advertising;
    const actNotSend = actSendStatus !== ActSendStatusEnum.Sent;
    const canEditAct = isAdvertisingType && actNotSend;
    const canSplitAct = processingType === ActProcessingTypeEnum.Manual;

    const [editingData, setEditingData] = useState<EditingData>({});
    const [isShowSplitConfirmation, showSplitConfirmation, hideSplitConfirmation] = useOnOffState(false);

    const dispatch = useDispatch();
    const trl = useTrls();

    const onSave = () => {
        const data = Object.fromEntries(
            Object.entries(editingData).map(([key, value]) => {
                if (typeof value === 'string') {
                    return [key, value.trim()];
                }
                return [key, value];
            })
        );
        void dispatch(editAct(data as unknown as EditActData, actId, () => setEditingData({})));
    };

    const onChange = (field: ActEditingField, value: string | number | undefined) => {
        setEditingData((state) => ({ ...state, [field]: value }));
    };

    const onSplit = () => {
        void dispatch(duplicateAct(actId)).then(() => hideSplitConfirmation());
    };

    const onActHistoryClick = () => {
        void dispatch(fetchActHistory(actId));
    };

    return (
        <TableRow>
            <TableCell>
                <div className={styles.tableCellGroup}>
                    <b>{trl('admin.actsReport.actsTable.label.actNumber')}: </b>
                    <DropDownClick
                        showCloseButton
                        render={() => <Invoices invoices={invoices} />}
                        placement={DownPlacement.BottomStart}
                    >
                        <Link>{actName}</Link>
                    </DropDownClick>
                </div>
                <Period act={act} onChange={onChange} values={editingData} canEditing={canEditAct} />
            </TableCell>
            <TableCell>
                <div className={styles.tableCellGroup}>
                    <b>{trl('admin.actsReport.actsTable.label.employerId')}: </b>
                    <EmployerLink employerId={employerId} />
                </div>
                <div className={styles.tableCellGroup}>
                    <b>{trl('admin.actsReport.actsTable.label.accountId')}: </b>
                    <AccountLink accountId={accountId} />
                </div>
            </TableCell>
            <TableCell>
                {canEditAct ? (
                    <EditingField
                        value={correctedAmount !== null ? correctedAmount : amount}
                        newValue={editingData[ActEditingField.CorrectedAmount]}
                        setNewValue={(amount) => {
                            setEditingData({ ...editingData, [ActEditingField.CorrectedAmount]: amount });
                        }}
                        resetNewValue={() => {
                            const newData = { ...editingData };
                            delete newData[ActEditingField.CorrectedAmount];
                            setEditingData(newData);
                        }}
                        size="medium"
                    >
                        <Amount amount={amount} correctedAmount={correctedAmount} />
                    </EditingField>
                ) : (
                    <Amount amount={amount} correctedAmount={correctedAmount} />
                )}
            </TableCell>
            <TableCell>
                {canEditAct ? (
                    <EditingField
                        value={documentNumber}
                        newValue={editingData[ActEditingField.DocumentNumber]}
                        setNewValue={(actNumber) => {
                            onChange(ActEditingField.DocumentNumber, actNumber);
                        }}
                        resetNewValue={() => {
                            onChange(ActEditingField.DocumentNumber, undefined);
                        }}
                    >
                        {documentNumber}
                    </EditingField>
                ) : (
                    <div className={styles.documentNumberCell}>{documentNumber}</div>
                )}
            </TableCell>
            <TableCell>
                <div className={styles.tableCellType}>
                    <div className={styles.tableCellGroup}>
                        <b>{trl('admin.actsReport.actsTable.label.type')}: </b>
                        <ActType type={type} />
                    </div>
                    <div className={styles.tableCellGroup}>
                        <b>{trl('admin.actsReport.actsTable.label.processingType')}: </b>
                        <ActProcessingType processingType={processingType} />
                    </div>
                    <div className={styles.tableCellGroup}>
                        <b>{trl('admin.actsReport.actsTable.label.advertisingServiceType')}: </b>
                        <ActAdvertisingServiceType advertisingServiceType={advertisingServiceType} />
                    </div>
                </div>
            </TableCell>
            <TableCell>
                <div>
                    <Status act={act} onChange={onChange} values={editingData} />
                    {error && (
                        <>
                            <HSpacing base={2} />
                            <span>
                                <ActError text={error} />
                            </span>
                        </>
                    )}
                    <VSpacing base={1} />
                </div>
                <SendStatus act={act} onChange={onChange} values={editingData} />
            </TableCell>
            <TableCell align="center">
                <Button kind={ButtonKind.Success} scale={ButtonScale.Small} onClick={onSave}>
                    {trl('save')}
                </Button>
                {canSplitAct && (
                    <>
                        <VSpacing base={2} />
                        <Button kind={ButtonKind.Secondary} scale={ButtonScale.Small} onClick={showSplitConfirmation}>
                            {trl('admin.actsReport.actsTable.act.button.split')}
                        </Button>
                        <ActSplitConfirmation
                            isVisible={isShowSplitConfirmation}
                            onCancel={hideSplitConfirmation}
                            onConfirm={onSplit}
                        />
                    </>
                )}
                <VSpacing base={3} />
                <Link onClick={onActHistoryClick}>
                    <div className={styles.actHistoryLink}>{trl('admin.actsReport.actsTable.act.button.history')}</div>
                </Link>
            </TableCell>
        </TableRow>
    );
};

export default EditingAct;
